import {
  isWorkspacePermissionCacheFresh,
  workspacePermissionCacheOwnerKey,
} from '../../workspace/utils/permission-cache-identity';

export type WorkspacePrincipalKind = 'group' | 'mate' | 'member';

export interface WorkspacePrincipalCacheScope {
  cacheOwnerKey: string;
  workspaceId: string;
}

export interface CachedWorkspacePrincipal {
  avatarUrl?: string | null;
  description?: string | null;
  email?: string | null;
  id: string;
  kind: WorkspacePrincipalKind;
  name: string;
  updatedAt: number;
  workspaceId: string;
}

interface WorkspacePrincipalCacheSnapshot {
  principals: CachedWorkspacePrincipal[];
  updatedAt: number;
  version: typeof WORKSPACE_PRINCIPAL_CACHE_VERSION;
  workspaceId: string;
}

export const WORKSPACE_PRINCIPAL_MEMBER_LIMIT = 256;
export const WORKSPACE_PRINCIPAL_GROUP_LIMIT = 64;
export const WORKSPACE_PRINCIPAL_MATE_LIMIT = 64;
export const WORKSPACE_PRINCIPAL_CACHE_VERSION = 1;

const STORAGE_PREFIX = 'streamlain:workspace-principals';

export function workspacePrincipalCacheScopeFromServer(
  workspaceId: string,
  server: unknown
): WorkspacePrincipalCacheScope | null {
  const serverRecord = server as
    | {
        ['account$']?: { value?: { id?: string | null } | null };
        baseUrl?: unknown;
        id?: unknown;
      }
    | null
    | undefined;
  const accountId = serverRecord?.['account$']?.value?.id;
  if (
    !serverRecord ||
    !accountId ||
    typeof serverRecord.baseUrl !== 'string' ||
    typeof serverRecord.id !== 'string'
  ) {
    return null;
  }
  return {
    cacheOwnerKey: workspacePermissionCacheOwnerKey({
      accountId,
      serverBaseUrl: serverRecord.baseUrl,
      serverId: serverRecord.id,
    }),
    workspaceId,
  };
}

export function readCachedWorkspacePrincipals(
  scope: WorkspacePrincipalCacheScope,
  now = Date.now()
): CachedWorkspacePrincipal[] {
  const storageKey = workspacePrincipalCacheStorageKey(scope);
  if (!storageKey) {
    return [];
  }
  try {
    const raw = globalThis.localStorage.getItem(storageKey);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as Partial<WorkspacePrincipalCacheSnapshot>;
    if (
      parsed.version !== WORKSPACE_PRINCIPAL_CACHE_VERSION ||
      parsed.workspaceId !== scope.workspaceId ||
      !Array.isArray(parsed.principals)
    ) {
      return [];
    }
    return parsed.principals.filter(
      principal =>
        isValidPrincipal(principal, scope.workspaceId) &&
        isWorkspacePermissionCacheFresh(principal.updatedAt, now)
    );
  } catch {
    return [];
  }
}

export function searchCachedWorkspacePrincipals(
  scope: WorkspacePrincipalCacheScope,
  options?: {
    kind?: WorkspacePrincipalKind;
    now?: number;
    query?: string;
  }
): CachedWorkspacePrincipal[] {
  const query = options?.query?.trim().toLowerCase() ?? '';
  return readCachedWorkspacePrincipals(scope, options?.now).filter(
    principal => {
      if (options?.kind && principal.kind !== options.kind) {
        return false;
      }
      if (!query) {
        return true;
      }
      return (
        principal.name.toLowerCase().includes(query) ||
        (principal.email?.toLowerCase().includes(query) ?? false)
      );
    }
  );
}

export function upsertCachedWorkspacePrincipals(
  scope: WorkspacePrincipalCacheScope | null,
  principals: CachedWorkspacePrincipal[],
  now = Date.now()
): void {
  if (!scope || principals.length === 0) {
    return;
  }
  const storageKey = workspacePrincipalCacheStorageKey(scope);
  if (!storageKey) {
    return;
  }
  const existing = readCachedWorkspacePrincipals(scope, now);
  const byKey = new Map<string, CachedWorkspacePrincipal>();
  for (const principal of existing) {
    byKey.set(principalCacheKey(principal), principal);
  }
  for (const principal of principals) {
    if (!isValidPrincipal(principal, scope.workspaceId)) {
      continue;
    }
    byKey.set(principalCacheKey(principal), {
      ...principal,
      updatedAt: Number.isFinite(principal.updatedAt)
        ? principal.updatedAt
        : now,
    });
  }

  const evicted = evictWorkspacePrincipals([...byKey.values()]);
  try {
    globalThis.localStorage.setItem(
      storageKey,
      JSON.stringify({
        principals: evicted,
        updatedAt: now,
        version: WORKSPACE_PRINCIPAL_CACHE_VERSION,
        workspaceId: scope.workspaceId,
      } satisfies WorkspacePrincipalCacheSnapshot)
    );
  } catch {
    // Opportunistic cache. Online GraphQL remains source of truth.
  }
}

export function memberToCachedWorkspacePrincipal(
  workspaceId: string,
  member: {
    avatarUrl?: string | null;
    email?: string | null;
    id: string;
    name?: string | null;
  },
  now = Date.now()
): CachedWorkspacePrincipal {
  return {
    avatarUrl: member.avatarUrl ?? null,
    email: member.email ?? null,
    id: member.id,
    kind: 'member',
    name: member.name || member.email || member.id,
    updatedAt: now,
    workspaceId,
  };
}

export function groupToCachedWorkspacePrincipal(
  group: {
    description?: string | null;
    id: string;
    name: string;
    workspaceId: string;
  },
  now = Date.now()
): CachedWorkspacePrincipal {
  return {
    description: group.description ?? null,
    id: group.id,
    kind: 'group',
    name: group.name,
    updatedAt: now,
    workspaceId: group.workspaceId,
  };
}

/** A workspace Mate as a grant target (kind 'mate'). */
export function mateToCachedWorkspacePrincipal(
  workspaceId: string,
  mate: { id: string; name: string },
  now = Date.now()
): CachedWorkspacePrincipal {
  return {
    id: mate.id,
    kind: 'mate',
    name: mate.name,
    updatedAt: now,
    workspaceId,
  };
}

function evictWorkspacePrincipals(
  principals: CachedWorkspacePrincipal[]
): CachedWorkspacePrincipal[] {
  const groups = principals.filter(principal => principal.kind === 'group');
  const members = principals.filter(principal => principal.kind === 'member');
  const mates = principals.filter(principal => principal.kind === 'mate');
  return [
    ...evictByUpdatedAt(members, WORKSPACE_PRINCIPAL_MEMBER_LIMIT),
    ...evictByUpdatedAt(groups, WORKSPACE_PRINCIPAL_GROUP_LIMIT),
    ...evictByUpdatedAt(mates, WORKSPACE_PRINCIPAL_MATE_LIMIT),
  ];
}

function evictByUpdatedAt(
  principals: CachedWorkspacePrincipal[],
  limit: number
): CachedWorkspacePrincipal[] {
  return principals
    .sort((left, right) => right.updatedAt - left.updatedAt)
    .slice(0, limit);
}

function isValidPrincipal(
  principal: Partial<CachedWorkspacePrincipal>,
  workspaceId: string
): principal is CachedWorkspacePrincipal {
  return (
    (principal.kind === 'member' ||
      principal.kind === 'group' ||
      principal.kind === 'mate') &&
    principal.workspaceId === workspaceId &&
    typeof principal.id === 'string' &&
    principal.id.length > 0 &&
    typeof principal.name === 'string' &&
    principal.name.length > 0 &&
    typeof principal.updatedAt === 'number' &&
    Number.isFinite(principal.updatedAt)
  );
}

function principalCacheKey(principal: CachedWorkspacePrincipal): string {
  return `${principal.kind}:${principal.id}`;
}

function workspacePrincipalCacheStorageKey(
  scope: WorkspacePrincipalCacheScope
): string | null {
  if (typeof globalThis.localStorage === 'undefined') {
    return null;
  }
  return `${STORAGE_PREFIX}:${scope.workspaceId}:${scope.cacheOwnerKey}`;
}
